import { OrderItemResponseDto } from 'client/widget-components/ecom/types/EcomSDK';
import React from 'react';
import styled from 'styled-components';
import { DATA_GRABS } from '../design';
import { COLORS, sp } from '../styles';
import { BookingStatus, useBookingDetails } from '../useBookingDetails';
import { useEcommStr } from '../useEcommStr';
import { digitalResourcesOf, formatCurrency } from '../utils';
import ItemCustomizationList from './ItemCustomizationList';
import LineItemDownloads from './LineItemDownloads';
import Pill, { PillType } from './Pill';
import ThemedText from './ThemedText';
import WarningNotice from './WarningNotice';

const Wrapper = styled.div`
    position: relative;
    max-inline-size: 100%;
`;

const Ctn = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;
    gap: ${sp(2.5)};
`;

const Image = styled.img`
    display: block;
    object-fit: cover;
    border-radius: 8px;
    inline-size: 110px;
    block-size: 110px;
    flex-shrink: 0;
`;

const Infos = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sp(1)};
    flex-grow: 1;
    min-width: 0;
`;

const Name = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
})``;

const OptionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.7em;
`;

const OptionName = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})``;

const OptionValue = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
    defaultTag: 'span' as const,
})``;

const Right = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${sp(3)};
    flex-shrink: 0;
`;

const PriceCtn = styled.div`
    display: flex;
    align-items: start;
    gap: 1em;
`;

const StaticQuantity = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})``;

const Subtotal = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})`
    text-align: end;
`;

const BookingNoticeContainer = styled.div`
    margin-block-start: ${sp(1.5)};
`;

const DownloadsContainer = styled.div`
    margin-block-start: ${sp(1.5)};
`;

const BookingConfirmedRow = styled.div.attrs({
    'data-grab': DATA_GRABS.dividers,
})`
    border: 1px solid ${COLORS.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${sp(1)};
    flex-wrap: wrap;
    min-block-size: 58px;
    padding: ${sp(1.5)} ${sp(2)};
    border-radius: 6px;
`;

const BookingId = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
    defaultTag: 'span' as const,
})``;

const pillTypeForStatus: Record<BookingStatus, PillType> = {
    accepted: 'success',
    pending: 'info',
    cancelled: 'neutral',
    rejected: 'neutral',
};

const pillLabelKeyForStatus: Record<BookingStatus, string> = {
    accepted: 'nativeEcommRuntime.booking.status.accepted',
    pending: 'nativeEcommRuntime.booking.status.pending',
    cancelled: 'nativeEcommRuntime.booking.status.cancelled',
    rejected: 'nativeEcommRuntime.booking.status.cancelled',
};

interface LineItemProps {
    item: OrderItemResponseDto;
    currency: string;
    orderId: string;
    canDownloadDigitalResources: boolean;
}

const LineItem: React.FC<LineItemProps> = ({
    item,
    currency,
    orderId,
    canDownloadDigitalResources,
}) => {
    const str = useEcommStr();
    const { booking, creationStatus } = useBookingDetails(item.booking);

    const shouldDisplayBookingNotice = item.booking !== null;
    const digitalResources = digitalResourcesOf(item);

    return (
        <Wrapper data-auto='cart-item-line-product'>
            <Ctn>
                {item.image_url && <Image src={item.image_url} alt='' />}

                <Infos>
                    <Name>{item.name}</Name>
                    {item.option_values.map((option) => (
                        <OptionRow
                            key={option.name}
                            data-auto={`cart-item-custom-field-${option.name}`}
                        >
                            <OptionName>{option.name}</OptionName>
                            <OptionValue>{option.value}</OptionValue>
                        </OptionRow>
                    ))}
                    {item.sku && (
                        <OptionRow data-auto='cart-item-sku'>
                            <OptionName>
                                {str(
                                    'nativeEcommRuntime.emails.common.lineItem.sku'
                                )}
                            </OptionName>
                            <OptionValue>{item.sku}</OptionValue>
                        </OptionRow>
                    )}
                </Infos>

                <Right>
                    <PriceCtn>
                        <StaticQuantity>× {item.quantity}</StaticQuantity>
                        <Subtotal data-auto='line-item-price'>
                            {formatCurrency(item.subtotal, currency)}
                        </Subtotal>
                    </PriceCtn>
                </Right>
            </Ctn>

            {item.product_customizations.length > 0 && (
                <ItemCustomizationList
                    customizations={item.product_customizations}
                    currency={currency}
                />
            )}

            {digitalResources.length > 0 && (
                <DownloadsContainer>
                    <LineItemDownloads
                        orderId={orderId}
                        lineItemId={item.id}
                        resources={digitalResources}
                        canDownload={canDownloadDigitalResources}
                    />
                </DownloadsContainer>
            )}

            {shouldDisplayBookingNotice && (
                <BookingNoticeContainer>
                    {creationStatus === 'FAILED' ? (
                        <WarningNotice
                            level='error'
                            text={str(
                                'nativeEcommRuntime.booking.failedNotice'
                            )}
                        />
                    ) : booking ? (
                        <BookingConfirmedRow>
                            <Pill
                                label={str(
                                    pillLabelKeyForStatus[booking.status]
                                )}
                                type={pillTypeForStatus[booking.status]}
                            />
                            <BookingId>
                                {str(
                                    'nativeEcommRuntime.booking.confirmationNumber',
                                    {
                                        number: booking.id,
                                    }
                                )}
                            </BookingId>
                        </BookingConfirmedRow>
                    ) : (
                        <WarningNotice
                            level='info'
                            text={str(
                                'nativeEcommRuntime.booking.processingNotice'
                            )}
                            isLoading
                        />
                    )}
                </BookingNoticeContainer>
            )}
        </Wrapper>
    );
};

export default LineItem;
